import React, { useState } from "react";
import { Button, Modal, SpaceBetween } from "@amzn/awsui-components-react";
import { getQuery } from "../../helpers/logUtils";
import JsonCompare from "./JsonCompare/JsonCompare";
import { AccountingEventsGroup, CanonicalBusinessEvent } from "../../types";
import {
  DigitalBusinessDocument,
  DigitalCostDocument,
} from "../../types/documents";
import {
  isAccountingEventsGroup,
  isCanonicalBusinessEvent,
  isDigitalBusinessDocument,
} from "../../helpers/typeChecks";
import ViewLogs from "./ViewLogs";

interface LogButtonsProps {
  identifier: string;
  stage: string;
  logGroups: string[];
  dateRange: [Date, Date];
  isDlq?: boolean;
  data?: (
    | AccountingEventsGroup
    | DigitalBusinessDocument
    | DigitalCostDocument
    | CanonicalBusinessEvent
  )[];
  collapseProductsConfig?: {
    show: boolean;
    onToggle: () => void;
  };
  versionViewConfig?: {
    show: boolean;
    isTopLevel: boolean;
    onToggle: () => void;
  };
}

const EventButtons: React.FC<LogButtonsProps> = ({
  identifier,
  stage,
  logGroups,
  dateRange,
  isDlq = false,
  data,
  collapseProductsConfig,
  versionViewConfig,
}) => {
  const logQuery = getQuery(identifier, isDlq);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isProductsExpanded, setIsProductsExpanded] = useState(true);

  const handleModalOpen = () => {
    setIsModalVisible(true);
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
  };

  const handleProductsToggle = () => {
    const newExpandedState = !isProductsExpanded;
    setIsProductsExpanded(newExpandedState);
    if (collapseProductsConfig?.onToggle) {
      collapseProductsConfig.onToggle();
    }
  };

  function getId(): string {
    if (!data?.length) return "";

    const firstItem = data[0];
    switch (true) {
      case isAccountingEventsGroup(firstItem):
        return (firstItem as AccountingEventsGroup).pk ?? "";
      case isDigitalBusinessDocument(firstItem):
        return (firstItem as DigitalBusinessDocument).id ?? "";
      case isCanonicalBusinessEvent(firstItem):
        return (firstItem as CanonicalBusinessEvent).pk ?? "";
      default:
        return "";
    }
  }

  // Only show the Compare JSON button if data exists and has items
  const hasComparableData = data && data.length > 0;

  return (
    <SpaceBetween alignItems="center" direction="horizontal" size="xxs">
      <ViewLogs
        stage={stage}
        logGroups={logGroups}
        logQuery={logQuery}
        dateRange={dateRange}
      />
      {hasComparableData && (
        <Button onClick={handleModalOpen}>Compare JSON</Button>
      )}
      {versionViewConfig?.show && (
        <Button
          onClick={versionViewConfig.onToggle}
          iconName={versionViewConfig.isTopLevel ? "view-vertical" : "view-horizontal"}
          variant="normal"
        >
          {versionViewConfig.isTopLevel ? "Nested Versions" : "Top-level Versions"}
        </Button>
      )}
      {collapseProductsConfig?.show && (
        <Button
          onClick={handleProductsToggle}
          iconName={isProductsExpanded ? "zoom-out" : "zoom-in"}
          variant="normal"
        >
          {isProductsExpanded ? "Collapse Products" : "Expand Products"}
        </Button>
      )}
      {hasComparableData && (
        <Modal
          visible={isModalVisible}
          onDismiss={handleModalClose}
          header={`JSON Comparison - ${getId()}`}
          size="max"
        >
          <JsonCompare
            isModalVisible={isModalVisible}
            objects={data}
          />
        </Modal>
      )}
    </SpaceBetween>
  );
};

export default EventButtons;
